import React, { useState } from 'react';
import { MarketKpiGrid } from './MarketKpiGrid';
import { MarketDataTable } from './MarketDataTable';
import { MarketBarChart } from './MarketBarChart';
import { MarketEmptyState } from './MarketEmptyState';
import {
  formatMoney, formatPct, formatRating, formatSharePrice, formatNumber, formatPlacing,
  getRatingBandTone, getValueBucketTone, DASH,
} from '../../utils/marketFormatters';

const METRIC_TOGGLES = [
  { key: 'winPct', label: 'Win %' },
  { key: 'top5Pct', label: 'Top-5 %' },
  { key: 'roi', label: 'Avg ROI' },
];

function MetricToggle({ value, onChange }) {
  return (
    <div className="mk-metric-toggle">
      {METRIC_TOGGLES.map(m => (
        <button
          key={m.key}
          type="button"
          className={`mk-metric-toggle-btn ${value === m.key ? 'mk-metric-toggle-btn--active' : ''}`}
          onClick={() => onChange(m.key)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function toChartData(groups, metric) {
  return (groups || []).map(g => ({
    label: g.groupKey,
    value: g[metric],
    sampleSize: g.settledRunnerCount,
  }));
}

const metricFormatter = (metric) => (v) => (metric === 'roi' ? formatPct(v) : formatPct(v));

function RunnerCell({ row }) {
  return (
    <div className="mk-runner-cell">
      <span className="mk-runner-name">{row.runnerName}</span>
      {row.runnerNumber != null && <span className="mk-runner-number">#{row.runnerNumber}</span>}
    </div>
  );
}

function RatingCell({ row }) {
  return <span className={`mk-pill mk-pill--${getRatingBandTone(row.ratingBand)}`}>{formatRating(row.displayRating)}</span>;
}

function ValueCell({ row }) {
  return <span className={`mk-pill mk-pill--${getValueBucketTone(row.valueLabel)}`}>{row.valueLabel}</span>;
}

const OPPORTUNITY_COLUMNS = [
  { key: 'runnerName', label: 'Runner', sortable: true, render: r => <RunnerCell row={r} /> },
  { key: 'slateName', label: 'Showdown', sortable: true },
  { key: 'raceNumber', label: 'Race', align: 'center', render: r => r.raceNumber ? `R${r.raceNumber}` : DASH },
  { key: 'displayRating', label: 'Rating', align: 'center', sortable: true, render: r => <RatingCell row={r} /> },
  { key: 'valueLabel', label: 'Value', align: 'center', render: r => <ValueCell row={r} /> },
  { key: 'sharePrice', label: 'Share Price', align: 'right', sortable: true, render: r => formatSharePrice(r.sharePrice) },
  { key: 'ownershipUserPct', label: 'Owned By %', align: 'right', sortable: true, render: r => formatPct(r.ownershipUserPct) },
  { key: 'jockey', label: 'Jockey' },
  { key: 'trainer', label: 'Trainer' },
];

const POPULAR_COLUMNS = [
  { key: 'runnerName', label: 'Runner', sortable: true, render: r => <RunnerCell row={r} /> },
  { key: 'slateName', label: 'Showdown', sortable: true },
  { key: 'raceNumber', label: 'Race', align: 'center', render: r => r.raceNumber ? `R${r.raceNumber}` : DASH },
  { key: 'ownershipUserPct', label: 'Owned By %', align: 'right', sortable: true, render: r => formatPct(r.ownershipUserPct) },
  { key: 'totalEquityPurchased', label: 'Total Equity', align: 'right', sortable: true, render: r => formatPct((r.totalEquityPurchased || 0) / 100, 0) },
  { key: 'displayRating', label: 'Rating', align: 'center', sortable: true, render: r => <RatingCell row={r} /> },
  { key: 'valueLabel', label: 'Value', align: 'center', render: r => <ValueCell row={r} /> },
  { key: 'finish', label: 'Status/Finish', align: 'center', render: r => (r.isSettled ? formatPlacing(r.finishingPosition) : r.status === 'live' ? 'Live' : 'Upcoming') },
];

export const MarketOverviewTab = ({ overview, loading }) => {
  const [ratingMetric, setRatingMetric] = useState('winPct');
  const [valueMetric, setValueMetric] = useState('top5Pct');

  const k = overview?.kpis || {};
  const kpiItems = [
    { label: 'Active Showdowns', value: formatNumber(k.activeShowdowns) },
    { label: 'Upcoming Showdowns', value: formatNumber(k.upcomingShowdowns) },
    { label: 'Runners Tracked', value: formatNumber(k.runnersTracked) },
    { label: 'Avg Spend Per Runner', value: formatMoney(k.avgSpendPerRunner), tooltip: 'Average dollars invested per owned runner across active showdowns.' },
    { label: 'Top-Rated Win %', value: formatPct(k.topRatedWinPct), tooltip: 'Win rate of settled 90+ rated runners.' },
    { label: 'Value Runner Top-5 %', value: formatPct(k.valueRunnerTop5Pct), tooltip: 'Top-5 rate of settled Bargain/Value/Fair runners.' },
    { label: 'Virtual Prizemoney Analysed', value: formatMoney(k.virtualPrizemoneyAnalysed) },
  ];

  return (
    <div className="mk-tab-content">
      <MarketKpiGrid items={kpiItems} />

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Best Current Opportunities</h2>
          <p className="mk-section-subtitle">Active and upcoming runners worth a look — high rating, good value, fair ownership.</p>
        </div>
        <MarketDataTable
          columns={OPPORTUNITY_COLUMNS}
          rows={overview?.bestOpportunities}
          getRowKey={r => `${r.slateId}-${r.raceId}-${r.runnerId}`}
          loading={loading}
          initialSortKey={null}
          emptyIcon="🔭"
          emptyTitle="No active or upcoming runner markets available right now"
          emptyMessage="Check back once the next showdown opens for entries."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Most Popular Runners</h2>
          <p className="mk-section-subtitle">Who the crowd is backing right now, by ownership share.</p>
        </div>
        <MarketDataTable
          columns={POPULAR_COLUMNS}
          rows={overview?.mostPopularRunners}
          getRowKey={r => `${r.slateId}-${r.raceId}-${r.runnerId}`}
          loading={loading}
          emptyIcon="👥"
          emptyTitle="No ownership data yet"
          emptyMessage="Popularity data will appear once players start building stables."
        />
      </section>

      <div className="mk-chart-grid">
        <section className="mk-section">
          <div className="mk-section-header mk-section-header--row">
            <div>
              <h2 className="mk-section-title">Rating Performance</h2>
              <p className="mk-section-subtitle">How each rating band actually performs, settled races only.</p>
            </div>
            <MetricToggle value={ratingMetric} onChange={setRatingMetric} />
          </div>
          {loading
            ? <MarketEmptyState icon="📊" message="Loading…" />
            : <MarketBarChart data={toChartData(overview?.ratingPerformance, ratingMetric)} valueFormatter={metricFormatter(ratingMetric)} />}
        </section>

        <section className="mk-section">
          <div className="mk-section-header mk-section-header--row">
            <div>
              <h2 className="mk-section-title">Value Badge Performance</h2>
              <p className="mk-section-subtitle">Is the value model actually finding winners? Settled races only.</p>
            </div>
            <MetricToggle value={valueMetric} onChange={setValueMetric} />
          </div>
          {loading
            ? <MarketEmptyState icon="📊" message="Loading…" />
            : <MarketBarChart data={toChartData(overview?.valueBadgePerformance, valueMetric)} valueFormatter={metricFormatter(valueMetric)} />}
        </section>
      </div>
    </div>
  );
};

export default MarketOverviewTab;
